import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatUtcAsThai } from '../../lib/datetime'
import { useEmployees } from '../../lib/employees'
import type { EmployeeRow } from '../../lib/employees'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { useSession } from '../../lib/SessionContext'

/* ============================================================================
   เข้าแทนพนักงาน + ประวัติการเข้าแทน — `/admin/delegate`
   API: backend/src/routes/adminDelegate.js · lib/impersonation.js

   ⚠️ ฟีเจอร์นี้เปิดให้อ่านข้อมูลส่วนบุคคล (เวลาทำงาน/การลา/OT) ของพนักงาน
      ทุกคน หน้าจึงต้องพูดให้ชัดว่า "ทุกครั้งถูกบันทึก" ไม่ใช่ซ่อนไว้ในเงื่อนไข
   ============================================================================ */

type HistoryRow = {
  id: number
  actorUser: string
  actorRole: string | null
  targetEmp: string
  targetName: string | null
  reason: string
  ip: string | null
  startedAt: string
  endedAt: string | null
}

type History = { items: HistoryRow[]; total: number; limit: number; offset: number }

/** '2026-10-25T04:12:33' (UTC จาก DB) → '2026-10-25 11:12' (เวลาไทย ค.ศ.) */
function thaiTime(utc: string | null): string {
  return utc ? formatUtcAsThai(utc) : '—'
}

export default function Delegate() {
  const { call, refresh } = useSession()
  const navigate = useNavigate()

  const [picked, setPicked] = useState<EmployeeRow | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [page, setPage] = useState(1)
  const [history, setHistory] = useState<History | null>(null)
  const [loading, setLoading] = useState(true)

  const pageSize = 10
  const loadHistory = useCallback(async () => {
    setLoading(true)
    try {
      setHistory(
        await call<History>(
          `/api/admin/delegate/history?limit=${pageSize}&offset=${(page - 1) * pageSize}`,
        ),
      )
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [call, page])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  async function start() {
    if (!picked) return
    setBusy(true)
    setError(null)
    try {
      await call('/api/admin/delegate/start', {
        method: 'POST',
        body: JSON.stringify({ empCode: picked.empCode, reason }),
      })
      // session เปลี่ยนไปแล้ว (cookie ใบใหม่มี actingAs) — ต้องโหลดใหม่ก่อนพาไปหน้า /me
      await refresh()
      navigate('/me/worktime')
    } catch (err) {
      setError(errorText(err))
      setBusy(false)
    }
  }

  return (
    <>
      <h1>เข้าแทนพนักงาน</h1>

      <section className="card">
        <p>
          เปิดหน้า <strong>“ของฉัน”</strong> ของพนักงานเพื่อดูสิ่งที่เจ้าตัวเห็น
          (เวลาทำงาน · การลา · OT) ใช้ตอนพนักงานแจ้งปัญหาแล้วต้องดูหน้าจอเดียวกัน
        </p>
        <p className="meta">
          ดูได้อย่างเดียว แก้อะไรไม่ได้ · <strong>ทุกครั้งถูกบันทึกชื่อผู้เข้า เวลา และเหตุผล</strong>
        </p>

        <div className="form-row">
          <EmployeePicker picked={picked} onPick={setPicked} />
        </div>

        <div className="form-row">
          <label className="kv">
            <span className="kv-label">เหตุผล (บังคับกรอก)</span>
            <input
              type="text"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น พนักงานแจ้งว่า OT เดือน ต.ค. ไม่ขึ้น"
            />
          </label>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="row-actions">
          <button type="button" onClick={start} disabled={busy || !picked || !reason.trim()}>
            {busy ? 'กำลังเข้า…' : 'เข้าแทนพนักงานคนนี้'}
          </button>
        </div>
      </section>

      <section className="card">
        <h2>ประวัติการเข้าแทน</h2>
        {loading && !history ? (
          <p className="meta">กำลังโหลด…</p>
        ) : history && history.items.length > 0 ? (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>เวลาเข้า</th>
                    <th>ออก</th>
                    <th>ผู้เข้า</th>
                    <th>เข้าแทน</th>
                    <th>เหตุผล</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {history.items.map((h) => (
                    <tr key={h.id}>
                      <td className="nowrap">{thaiTime(h.startedAt)}</td>
                      <td className="nowrap">
                        {h.endedAt ? (
                          thaiTime(h.endedAt)
                        ) : (
                          /* ไม่ได้กดออกเอง — ปล่อยให้ cookie หมดอายุ ไม่ใช่ข้อผิดพลาด */
                          <span className="meta">ไม่ได้กดออก</span>
                        )}
                      </td>
                      <td className="nowrap">
                        {h.actorUser}
                        {h.actorRole && <span className="meta"> · {h.actorRole}</span>}
                      </td>
                      <td className="nowrap">
                        {h.targetEmp}
                        {h.targetName && <span className="meta"> · {h.targetName}</span>}
                      </td>
                      <td className="cell-clip">{h.reason}</td>
                      <td className="mono">{h.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              page={page}
              pageSize={pageSize}
              total={history.total}
              loading={loading}
              onPage={setPage}
            />
          </>
        ) : (
          <p className="meta">ยังไม่เคยมีการเข้าแทนพนักงาน</p>
        )}
      </section>
    </>
  )
}

/** ค้นพนักงาน — ท่าเดียวกับ PersonReport เพื่อให้หน้าตาและพฤติกรรมตรงกัน */
function EmployeePicker({
  picked,
  onPick,
}: {
  picked: EmployeeRow | null
  onPick: (employee: EmployeeRow) => void
}) {
  const employees = useEmployees()
  const [term, setTerm] = useState('')
  const [list, setList] = useState<EmployeeRow[] | null>(null)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      employees
        // เข้าแทนได้เฉพาะคนที่ยังทำงานอยู่ (backend เช็คซ้ำ) — ไม่ต้องโชว์คนลาออก
        .list({ q: term, status: 'active', pageSize: 15 })
        .then((r) => setList(r.employees))
        .catch(() => setList([]))
    }, 200)
    return () => clearTimeout(id)
  }, [employees, term, open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div className="combo emp-search" ref={box}>
      <input
        type="search"
        value={open ? term : picked ? `${picked.empCode} · ${picked.fullName ?? ''}` : ''}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => {
          setTerm('')
          setOpen(true)
        }}
        placeholder="ค้นด้วยรหัส หรือชื่อ–สกุล"
        aria-label="เลือกพนักงานที่จะเข้าแทน"
      />
      {open && (
        <ul className="combo-list">
          {list === null && <li className="combo-item">กำลังค้น…</li>}
          {list?.length === 0 && <li className="combo-item">ไม่พบพนักงานที่ตรงกับคำค้น</li>}
          {list?.map((e) => (
            <li key={e.empCode}>
              <button
                type="button"
                className={
                  e.empCode === picked?.empCode ? 'combo-item combo-item--active' : 'combo-item'
                }
                onClick={() => {
                  onPick(e)
                  setOpen(false)
                }}
              >
                <span className="emp-code">{e.empCode}</span> {e.fullName ?? ''}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
